import React from 'react';
import { useNavigate } from 'react-router-dom';
import { showError } from '../../services/toastServices';

const TaskDetailModal = ({ task, isOpen, onClose }) => {
    const navigate = useNavigate();

    if (!isOpen || !task) return null;

    const handleGoToTask = () => {
        // Intentar obtener el categoryId de diferentes formas
        let categoryId = null;
        
        if (task.categoryId) {
            categoryId = task.categoryId;
        } else if (task.project && task.project.categoryId) {
            categoryId = task.project.categoryId;
        } else if (task.project && task.project.category && task.project.category.id) {
            categoryId = task.project.category.id;
        }
        
        if (categoryId) {
            // Navegar con state para expandir proyecto y tarea
            navigate(`/category-projects/${categoryId}`, {
                state: {
                    expandProjectId: task.projectId,
                    expandTaskId: task.id
                }
            });
            onClose();
        } else {
            console.error('No se encontró categoryId en la tarea');
            showError('No se puede navegar a la tarea - falta información de categoría');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-start p-6 border-b border-gray-200">
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900">{task.name}</h2>
                        <div className="flex gap-2 mt-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                task.status === 0 ? 'bg-blue-100 text-blue-800' :
                                task.status === 1 ? 'bg-yellow-100 text-yellow-800' :
                                task.status === 2 ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {task.status === 0 ? 'Nueva' : task.status === 1 ? 'En Progreso' : task.status === 2 ? 'Completada' : 'Cancelada'}
                            </span>
                            {task.size && (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                    Tamaño: {task.size}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleGoToTask}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Ir a la tarea
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Descripción */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Descripción</h3>
                        {task.richDescription ? (
                            <div className="prose max-w-none text-gray-700 bg-gray-50 p-4 rounded-lg">
                                {task.richDescription}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">Sin descripción</p>
                        )}
                    </div>

                    {/* Deadline */}
                    {task.deadline && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Fecha límite</h3>
                            <p className="text-gray-700">
                                {new Date(task.deadline).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>
                    )}

                    {/* Subtareas */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Subtareas</h3>
                        {task.subtasks && task.subtasks.length > 0 ? (
                            <ul className="space-y-2">
                                {task.subtasks.map((subtask) => (
                                    <li key={subtask.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                            subtask.status === 2 ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                        }`}>
                                            {subtask.status === 2 && (
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                        <span className={`flex-1 ${subtask.status === 2 ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                            {subtask.name}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 italic">Sin subtareas</p>
                        )}
                    </div>

                    {/* Recursos */}
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recursos</h3>
                        {task.taskResources && task.taskResources.length > 0 ? (
                            <ul className="space-y-2">
                                {task.taskResources.map((resource) => (
                                    <li key={resource.id} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                                        <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900">{resource.name}</p>
                                            {resource.url && (
                                                <a
                                                    href={resource.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {resource.url}
                                                </a>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-500 italic">Sin recursos</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskDetailModal;
